import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, Download, Wallet } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "weekly_timesheet", label: "Weekly Timesheet Report" },
  { key: "employee_hours", label: "Employee Hours Report" },
  { key: "employee_wage_cost", label: "Employee Wage Cost Report" },
  { key: "fully_loaded_cost", label: "Fully Loaded Employee Cost Report" },
  { key: "site_profitability", label: "Site Profitability Report" },
  { key: "customer_profitability", label: "Customer Profitability Report" },
  { key: "budget_vs_actual", label: "Budget vs Actual Report" },
  { key: "materials", label: "Materials & Additional Cost Report" },
  { key: "overtime", label: "Overtime Report" },
  { key: "leave_balance", label: "Annual Leave Balance Report" },
  { key: "sick_leave", label: "Sick Leave Report" },
  { key: "audit", label: "Audit Log Export" },
];

export default function Reports() {
  const [busy, setBusy] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [supplier, setSupplier] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

  const download = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      const params = { format };
      if (start) params.start = start;
      if (end) params.end = end;
      if (key === "materials" && supplier !== "all") params.supplier = supplier;
      const res = await api.get(`/reports/${key}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${key}.${format === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };

  return (
    <AdminLayout nav={adminNav} title="Reports">
      <PageHeader title="Reports & Exports" subtitle="Filter by date range, then download to CSV or Excel" />

      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-6 flex flex-wrap items-end gap-4">
        <div><Label className="text-xs">From date</Label><Input data-testid="report-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 w-44" /></div>
        <div><Label className="text-xs">To date</Label><Input data-testid="report-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 w-44" /></div>
        {(start || end) && <Button variant="ghost" size="sm" data-testid="clear-dates" onClick={() => { setStart(""); setEnd(""); }}>Clear</Button>}
        <div><Label className="text-xs">Supplier (materials report)</Label>
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger data-testid="report-supplier" className="mt-1 h-10 w-52"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All suppliers</SelectItem>{suppliers.map((s) => { const n = typeof s === "string" ? s : s.name; return <SelectItem key={n} value={n}>{n}</SelectItem>; })}</SelectContent>
          </Select>
        </div>
        <span className="text-xs text-zinc-400">Leave blank for all-time</span>
      </div>

      {/* One-click payroll export */}
      <div data-testid="report-payroll" className="bg-slate-900 text-white rounded-lg p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-amber-500/20 flex items-center justify-center"><Wallet className="h-6 w-6 text-amber-400" /></div>
          <div>
            <div className="font-heading font-bold">Payroll Export</div>
            <div className="text-sm text-zinc-300">Per employee: hours, wages, employer PRSI, sick pay & fully-loaded cost{start || end ? ` (${start || "start"} → ${end || "today"})` : ""}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="csv-payroll" size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" disabled={busy === "payroll-csv"} onClick={() => download("payroll", "csv")}><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
          <Button data-testid="xlsx-payroll" size="sm" className="bg-amber-500 text-black font-bold" disabled={busy === "payroll-xlsx"} onClick={() => download("payroll", "xlsx")}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {REPORTS.map((r) => (
          <div key={r.key} data-testid={`report-${r.key}`} className="bg-white border border-zinc-200 rounded-md p-4 flex items-center justify-between">
            <div className="flex items-center gap-3"><FileText className="h-5 w-5 text-zinc-400" /><span className="font-medium text-sm">{r.label}</span></div>
            <div className="flex gap-2">
              <Button data-testid={`csv-${r.key}`} size="sm" variant="outline" disabled={busy === `${r.key}-csv`} onClick={() => download(r.key, "csv")}><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
              <Button data-testid={`xlsx-${r.key}`} size="sm" className="bg-emerald-600 text-white" disabled={busy === `${r.key}-xlsx`} onClick={() => download(r.key, "xlsx")}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Excel</Button>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
